/**
 * Système d'export avancé pour les rapports RH
 * Support PDF, Excel, et exports combinés
 */

import { ensureSubscriptionActionAllowed } from '../../utils/subscriptionActionGuard';
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Select,
  Checkbox,
  Row,
  Col,
  Typography,
  Space,
  Progress,
  Alert,
  Divider,
  Input,
  Form,
  Steps,
  theme,
  message
} from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SettingOutlined,
  LoadingOutlined,
  MailOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../api/apiClient';
import API_CONFIG from '../../config/api';
import { reportHeaders, reportError, useHRReportScope } from './hrReportsApi';

const { Text, Title } = Typography;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

interface ExportConfig {
  format: 'pdf' | 'excel' | 'both';
  reportTypes: string[];
  includeCharts: boolean;
  includeRawData: boolean;
  template: string;
  fileName: string;
  emailRecipients: string[];
  schedule: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    time: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
  };
  filters: any;
}

interface HRReportExporterProps {
  visible: boolean;
  onClose: () => void;
  reportData: any;
  currentFilters: any;
  period: string;
  selectedReport: string;
}

const HRReportExporter: React.FC<HRReportExporterProps> = ({
  visible,
  onClose,
  reportData,
  currentFilters,
  period,
  selectedReport,
}) => {
  const scope = useHRReportScope();
  const { token: themeToken } = theme.useToken();
  const request = useRef<AbortController | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    format: 'pdf',
    reportTypes: [selectedReport],
    includeCharts: true,
    includeRawData: false,
    template: 'standard',
    fileName: `rapport-rh-${dayjs().format('YYYY-MM-DD')}`,
    emailRecipients: [],
    schedule: {
      enabled: false,
      frequency: 'monthly',
      time: '09:00'
    },
    filters: currentFilters
  });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [form] = Form.useForm();
  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setExportConfig(previous => ({ ...previous, reportTypes: [selectedReport], filters: currentFilters }));
    } else request.current?.abort();
  }, [visible, selectedReport, currentFilters]);
  useEffect(() => () => { request.current?.abort(); }, []);

  const reportTypeOptions = [
    { label: 'Déclaration CNSS', value: 'cnss', icon: <FilePdfOutlined /> },
    { label: 'Déclaration IUTS', value: 'iuts', icon: <FilePdfOutlined /> },
    { label: 'Rapport d\'Audit', value: 'audit', icon: <FilePdfOutlined /> },
    { label: 'Dashboard KPI', value: 'dashboard', icon: <FileExcelOutlined /> }
  ];

  const templateOptions = [
    { 
      label: 'Standard', 
      value: 'standard', 
      description: 'Template officiel conforme aux réglementations' 
    },
    { 
      label: 'Détaillé', 
      value: 'detailed', 
      description: 'Rapport complet avec analyses approfondies' 
    },
    { 
      label: 'Résumé Exécutif', 
      value: 'executive', 
      description: 'Vue d\'ensemble pour la direction' 
    },
    { 
      label: 'Personnalisé', 
      value: 'custom', 
      description: 'Template personnalisé uploadé par l\'utilisateur' 
    }
  ];

  const handleExport = async () => {
    if (request.current || !scope.allowed || !scope.companyId) return;
    const controller = new AbortController();
    request.current = controller;
    setExporting(true);
    setExportProgress(0);

    try {
      if ((exportConfig.format === 'excel' || exportConfig.format === 'both') && !(await ensureSubscriptionActionAllowed('data_export'))) return;
      if (controller.signal.aborted) return;
      if (!exportConfig.reportTypes.length) throw new Error('Choisissez au moins un type de rapport.');
      setExportProgress(15);
      if (exportConfig.format === 'pdf' || exportConfig.format === 'both') {
        const pdfBlob = await generatePDFReport(exportConfig);
        if (controller.signal.aborted) return;
        downloadFile(pdfBlob, `${exportConfig.fileName}.pdf`, 'application/pdf');
        setExportProgress(50);
      }

      if (exportConfig.format === 'excel' || exportConfig.format === 'both') {
        const excelBlob = await generateExcelReport(exportConfig);
        if (controller.signal.aborted) return;
        downloadFile(excelBlob, `${exportConfig.fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        setExportProgress(75);
      }

      // Envoi par email si configuré
      if (exportConfig.emailRecipients.length > 0) {
        if (controller.signal.aborted) return;
        await sendEmailReports(exportConfig);
      }

      // Configuration de la planification si activée
      if (exportConfig.schedule.enabled) {
        if (controller.signal.aborted) return;
        await scheduleReports(exportConfig);
      }

      if (controller.signal.aborted) return;
      setExportProgress(100);
      message.success('Export réalisé avec succès !');
      onClose();
    } catch (error) {
      if (!controller.signal.aborted) message.error(reportError(error));
    } finally {
      request.current = null;
      if (!controller.signal.aborted) { setExporting(false); setExportProgress(0); }
    }
  };

  const generatePDFReport = async (config: ExportConfig): Promise<Blob> => {
    try {
      // Récupération du token d'authentification
      let token = localStorage.getItem('authToken');
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Appel API pour générer le PDF
      const response = await apiClient.post(API_CONFIG.url('/api/hr/exports/pdf'), {
        period,
        template: config.template,
        reportTypes: config.reportTypes,
        includeCharts: config.includeCharts,
        fileName: config.fileName,
        filters: config.filters
      }, {
        headers: reportHeaders(scope.companyId), signal: request.current?.signal, timeout: 120000,
        responseType: 'blob' // Important pour recevoir un blob
      });

      return new Blob([response.data], { type: 'application/pdf' });
    } catch (error) {
      throw error;
    }
  };

  const generateExcelReport = async (config: ExportConfig): Promise<Blob> => {
    try {
      // Récupération du token d'authentification
      let token = localStorage.getItem('authToken');
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Appel API pour générer l'Excel - NOUVEAU SYSTÈME OPTIMISÉ
      const onlyType = config.reportTypes.length === 1 ? config.reportTypes[0] : '';
      const optimized = config.reportTypes.every(type => type === 'cnss' || type === 'iuts');
      const endpoint = optimized ? `/api/hr/optimized-exports/${onlyType || 'combined'}` : '/api/hr/exports/excel';
      const response = await apiClient.post(API_CONFIG.url(endpoint), {
        period,
        template: config.template,
        reportTypes: config.reportTypes,
        includeRawData: config.includeRawData,
        fileName: config.fileName,
        filters: config.filters
      }, {
        headers: reportHeaders(scope.companyId), signal: request.current?.signal, timeout: 120000,
        responseType: 'blob' // Important pour recevoir un blob
      });

      return new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
    } catch (error) {
      throw error;
    }
  };

  const downloadFile = (blob: Blob, fileName: string, mimeType: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const sendEmailReports = async (config: ExportConfig): Promise<void> => {
    try {
      let token = localStorage.getItem('authToken');
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      await apiClient.post(API_CONFIG.url('/api/hr/exports/email'), {
        recipients: config.emailRecipients,
        reportConfig: { ...config, period },
        message: 'Rapport RH automatique généré'
      }, {
        headers: reportHeaders(scope.companyId), signal: request.current?.signal, timeout: 120000,
      });

      message.success(`Rapports envoyés par email à ${config.emailRecipients.length} destinataire(s)`);
    } catch (error) {
      throw error;
    }
  };

  const scheduleReports = async (config: ExportConfig): Promise<void> => {
    try {
      let token = localStorage.getItem('authToken');
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      await apiClient.post(API_CONFIG.url('/api/hr/exports/schedule'), {
        name: `Export automatique - ${config.fileName}`,
        config: {
          period,
          format: config.format,
          template: config.template,
          fileName: config.fileName
        },
        frequency: config.schedule.frequency,
        timeConfig: {
          time: config.schedule.time,
          dayOfWeek: config.schedule.dayOfWeek,
          dayOfMonth: config.schedule.dayOfMonth
        },
        emailRecipients: config.emailRecipients
      }, {
        headers: reportHeaders(scope.companyId), signal: request.current?.signal, timeout: 120000,
      });

      message.success(`Planification configurée: export ${config.schedule.frequency}`);
    } catch (error) {
      throw error;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Configuration générale
        return (
          <div>
            <Title level={4}>Configuration de l'export</Title>
            
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item label="Format d'export">
                  <Select
                    value={exportConfig.format}
                    onChange={(format) => setExportConfig(prev => ({ ...prev, format }))}
                    style={{ width: '100%' }}
                  >
                    <Option value="pdf">
                      <FilePdfOutlined style={{ color: themeToken.colorErrorText }} /> PDF uniquement
                    </Option>
                    <Option value="excel">
                      <FileExcelOutlined style={{ color: themeToken.colorSuccessText }} /> Excel uniquement
                    </Option>
                    <Option value="both">
                      <DownloadOutlined style={{ color: themeToken.colorInfoText }} /> PDF + Excel
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Template">
                  <Select
                    value={exportConfig.template}
                    onChange={(template) => setExportConfig(prev => ({ ...prev, template }))}
                    style={{ width: '100%' }}
                  >
                    {templateOptions.map(template => (
                      <Option key={template.value} value={template.value}>
                        {template.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Types de rapports à inclure">
              <Checkbox.Group
                options={reportTypeOptions}
                value={exportConfig.reportTypes}
                onChange={(reportTypes) => setExportConfig(prev => ({ ...prev, reportTypes }))}
              />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Checkbox
                  checked={exportConfig.includeCharts}
                  onChange={(e) => setExportConfig(prev => ({ ...prev, includeCharts: e.target.checked }))}
                >
                  Inclure les graphiques et visualisations
                </Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox
                  checked={exportConfig.includeRawData}
                  onChange={(e) => setExportConfig(prev => ({ ...prev, includeRawData: e.target.checked }))}
                >
                  Inclure les données brutes (Excel)
                </Checkbox>
              </Col>
            </Row>

            <Form.Item label="Nom du fichier">
              <Input
                value={exportConfig.fileName}
                onChange={(e) => setExportConfig(prev => ({ ...prev, fileName: e.target.value }))}
                placeholder="rapport-rh-2025-01"
              />
            </Form.Item>
          </div>
        );

      case 1: // Configuration email
        return (
          <div>
            <Title level={4}>Envoi par email (optionnel)</Title>
            
            <Alert
              message="Configuration email"
              description="Les rapports peuvent être envoyés automatiquement par email aux destinataires configurés."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item label="Destinataires email">
              <Select
                mode="tags"
                placeholder="Ajouter des adresses email"
                value={exportConfig.emailRecipients}
                onChange={(emailRecipients) => setExportConfig(prev => ({ ...prev, emailRecipients }))}
                style={{ width: '100%' }}
              >
                <Option value="rh@entreprise.bf">rh@entreprise.bf</Option>
                <Option value="direction@entreprise.bf">direction@entreprise.bf</Option>
                <Option value="compta@entreprise.bf">compta@entreprise.bf</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Message personnalisé">
              <TextArea
                rows={4}
                placeholder="Message à inclure dans l'email (optionnel)"
              />
            </Form.Item>
          </div>
        );

      case 2: // Planification
        return (
          <div>
            <Title level={4}>Planification automatique (optionnel)</Title>
            
            <Checkbox
              checked={exportConfig.schedule.enabled}
              onChange={(e) => setExportConfig(prev => ({ 
                ...prev, 
                schedule: { ...prev.schedule, enabled: e.target.checked }
              }))}
              style={{ marginBottom: 16 }}
            >
              Activer la génération automatique des rapports
            </Checkbox>

            {exportConfig.schedule.enabled && (
              <div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Fréquence">
                      <Select
                        value={exportConfig.schedule.frequency}
                        onChange={(frequency) => setExportConfig(prev => ({ 
                          ...prev, 
                          schedule: { ...prev.schedule, frequency }
                        }))}
                      >
                        <Option value="daily">Quotidienne</Option>
                        <Option value="weekly">Hebdomadaire</Option>
                        <Option value="monthly">Mensuelle</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item label="Heure">
                      <Input
                        type="time"
                        value={exportConfig.schedule.time}
                        onChange={(e) => setExportConfig(prev => ({ 
                          ...prev, 
                          schedule: { ...prev.schedule, time: e.target.value }
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  {exportConfig.schedule.frequency === 'weekly' && (
                    <Col xs={24} md={8}>
                      <Form.Item label="Jour de la semaine">
                        <Select
                          value={exportConfig.schedule.dayOfWeek}
                          onChange={(dayOfWeek) => setExportConfig(prev => ({ 
                            ...prev, 
                            schedule: { ...prev.schedule, dayOfWeek }
                          }))}
                        >
                          <Option value={1}>Lundi</Option>
                          <Option value={2}>Mardi</Option>
                          <Option value={3}>Mercredi</Option>
                          <Option value={4}>Jeudi</Option>
                          <Option value={5}>Vendredi</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  {exportConfig.schedule.frequency === 'monthly' && (
                    <Col xs={24} md={8}>
                      <Form.Item label="Jour du mois">
                        <Select
                          value={exportConfig.schedule.dayOfMonth}
                          onChange={(dayOfMonth) => setExportConfig(prev => ({ 
                            ...prev, 
                            schedule: { ...prev.schedule, dayOfMonth }
                          }))}
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <Option key={day} value={day}>{day}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <Space>
          <DownloadOutlined />
          <span>Export Avancé des Rapports RH</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="Configuration" icon={<SettingOutlined />} />
        <Step title="Email" icon={<MailOutlined />} />
        <Step title="Planification" icon={<CalendarOutlined />} />
      </Steps>

      {exporting ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <LoadingOutlined style={{ fontSize: 48, color: themeToken.colorInfoText, marginBottom: 16 }} />
          <Title level={4}>Export en cours...</Title>
          <Progress percent={exportProgress} status="active" />
          <Text type="secondary">
            Génération des rapports selon votre configuration
          </Text>
        </div>
      ) : (
        <Form form={form} layout="vertical">
          {renderStepContent()}
        </Form>
      )}

      <Divider />

      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={onClose}>
            Annuler
          </Button>
          
          {currentStep > 0 && (
            <Button onClick={() => setCurrentStep(currentStep - 1)}>
              Précédent
            </Button>
          )}
          
          {currentStep < 2 ? (
            <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
              Suivant
            </Button>
          ) : (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
            >
              Lancer l'export
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default HRReportExporter;
